/**
 * Minimal markdown -> Word blocks for `insert_text` (spec §4): headings, paragraphs, bullet and numbered
 * lists, **bold** and *italic* / _italic_. Anything else is inserted as literal text. Never HTML.
 */

export interface Run {
  text: string;
  bold: boolean;
  italic: boolean;
}

export type BlockKind = "paragraph" | "heading" | "bullet" | "number";

export interface Block {
  kind: BlockKind;
  /** 1–6 for headings, otherwise 0. */
  level: number;
  runs: Run[];
}

const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBER = /^\s*\d{1,3}[.)]\s+(.*)$/;

/** Split inline text into runs on `**bold**`, `*italic*` and `_italic_` (non-nested beyond bold+italic). */
export function parseInline(text: string): Run[] {
  const runs: Run[] = [];
  let bold = false;
  let italic = false;
  let buf = "";
  const push = (): void => {
    if (!buf) return;
    const last = runs[runs.length - 1];
    if (last !== undefined && last.bold === bold && last.italic === italic) last.text += buf;
    else runs.push({ text: buf, bold, italic });
    buf = "";
  };
  // A marker only toggles when its closing partner exists later in the text; otherwise it is literal.
  const hasCloser = (marker: string, from: number): boolean => text.indexOf(marker, from) !== -1;
  let i = 0;
  while (i < text.length) {
    if (text.startsWith("**", i) && (bold || hasCloser("**", i + 2))) {
      push();
      bold = !bold;
      i += 2;
      continue;
    }
    const ch = text[i] as string;
    if ((ch === "*" || ch === "_") && (italic || hasCloser(ch, i + 1))) {
      const prev = i > 0 ? (text[i - 1] as string) : " ";
      const next = i + 1 < text.length ? (text[i + 1] as string) : " ";
      // Snake_case and 2*3 are not emphasis: an opener must not follow a word char, a closer must not precede one.
      const wordish = /[\p{L}\p{N}]/u;
      const opens = !italic && !wordish.test(prev) && next !== " ";
      const closes = italic && !wordish.test(next) && prev !== " ";
      if (opens || closes) {
        push();
        italic = !italic;
        i += 1;
        continue;
      }
    }
    buf += ch;
    i += 1;
  }
  push();
  return runs.length > 0 ? runs : [{ text: "", bold: false, italic: false }];
}

export function parseMarkdown(markdown: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  const flush = (): void => {
    if (paragraph.length === 0) return;
    blocks.push({ kind: "paragraph", level: 0, runs: parseInline(paragraph.join(" ")) });
    paragraph = [];
  };
  for (const rawLine of markdown.replace(/\r\n?/g, "\n").split("\n")) {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      flush();
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      blocks.push({ kind: "heading", level: (heading[1] as string).length, runs: parseInline(heading[2] as string) });
      continue;
    }
    const bullet = BULLET.exec(line);
    if (bullet) {
      flush();
      blocks.push({ kind: "bullet", level: 0, runs: parseInline(bullet[1] as string) });
      continue;
    }
    const number = NUMBER.exec(line);
    if (number) {
      flush();
      blocks.push({ kind: "number", level: 0, runs: parseInline(number[1] as string) });
      continue;
    }
    paragraph.push(line.trim());
  }
  flush();
  return blocks;
}

/** Word built-in style for a block (`Paragraph.styleBuiltIn`, WordApi 1.3), or null to keep the host style. */
export function builtInStyle(block: Block): string | null {
  if (block.kind === "heading") return `Heading${block.level}`;
  if (block.kind === "bullet") return "ListBullet";
  if (block.kind === "number") return "ListNumber";
  return null;
}
